import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLocationCtx } from "../../context/LocationContext";
import { MapPin, CaretDown, MagnifyingGlass, X, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export function LocationSelector({ className = "" }) {
  const { loc, loading, setPincode, clear } = useLocationCtx();
  const [open, setOpen] = useState(false);
  const [pin, setPin] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const label = loc?.serviceable
    ? `${loc.locality ? loc.locality + ", " : ""}${loc.city}`
    : loc && !loc.serviceable ? `${loc.pincode} · Unavailable` : "Select location";

  const apply = async () => {
    try {
      const r = await setPincode(pin);
      if (r.serviceable) { toast.success(`Location set to ${r.city}`); setOpen(false); }
      setPin("");
    } catch (e) { toast.error(e.message || "Enter a valid pincode"); }
  };

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button onClick={() => setOpen((o) => !o)} data-testid="header-location-btn"
        className="flex items-center gap-2 h-11 px-3.5 rounded-xl border border-[#E2E8F0] bg-white hover:border-[#4F46E5] transition-colors duration-200 w-full">
        <MapPin size={18} weight="fill" className="text-[#4F46E5] shrink-0" />
        <span className={`text-sm font-semibold truncate ${loc?.serviceable ? "text-[#1E293B]" : "text-[#475569]"}`}>{label}</span>
        <CaretDown size={14} weight="bold" className="text-[#94a3b8] ml-auto shrink-0" />
      </button>

      {open && (
        <div className="absolute left-0 top-[calc(100%+8px)] w-[300px] bg-white rounded-2xl shadow-2xl border border-[#E2E8F0] p-4 z-[60]" data-testid="location-popover">
          <div className="flex items-center justify-between mb-3">
            <p className="font-[Outfit] font-bold text-sm">Your location</p>
            <button onClick={() => setOpen(false)}><X size={16} className="text-[#94a3b8]" /></button>
          </div>
          <div className="flex gap-2">
            <input value={pin} maxLength={6} inputMode="numeric" autoFocus data-testid="location-pincode-input"
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              onKeyDown={(e) => e.key === "Enter" && apply()}
              placeholder="Enter 6-digit pincode"
              className="flex-1 h-10 px-3 rounded-xl border border-[#CBD5E1] text-sm outline-none focus:border-[#4F46E5]" />
            <button onClick={apply} disabled={loading} data-testid="location-apply-btn"
              className="h-10 px-4 rounded-xl bg-[#4F46E5] hover:bg-[#4338CA] text-white text-sm font-bold">
              {loading ? "…" : "Go"}
            </button>
          </div>

          {loc && loc.serviceable && (
            <div className="mt-3 flex items-start gap-2 text-xs text-[#166534] bg-[#166534]/8 rounded-xl px-3 py-2" data-testid="location-serviceable">
              <CheckCircle size={15} weight="fill" className="shrink-0 mt-0.5" />
              <span>Serving <b>{loc.locality ? loc.locality + ", " : ""}{loc.city}</b> ({loc.pincode}). {loc.services.length} service{loc.services.length !== 1 ? "s" : ""} available.</span>
            </div>
          )}
          {loc && !loc.serviceable && (
            <div className="mt-3 flex items-start gap-2 text-xs text-[#9A3412] bg-[#FFF7ED] border border-[#FED7AA] rounded-xl px-3 py-2" data-testid="location-unavailable">
              <WarningCircle size={15} weight="fill" className="shrink-0 mt-0.5" />
              <span>We're not in <b>{loc.pincode}</b> yet. Try another pincode or <a href={`mailto:support@fixigohomeservices.in?subject=Notify me — ${loc.pincode}`} className="underline font-semibold">notify me</a>.</span>
            </div>
          )}
          {loc && (
            <button onClick={() => { clear(); toast.success("Location cleared"); }} className="mt-3 text-xs text-[#475569] hover:text-[#4F46E5] font-semibold" data-testid="location-clear">Clear location</button>
          )}
        </div>
      )}
    </div>
  );
}

export function HeaderSearch({ className = "" }) {
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const go = () => navigate(q.trim() ? `/services?q=${encodeURIComponent(q.trim())}` : "/services");
  return (
    <div className={`relative ${className}`}>
      <MagnifyingGlass size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#94a3b8]" />
      <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && go()}
        data-testid="header-search-input" placeholder="Search for 'AC service'"
        className="w-full h-11 pl-11 pr-3 rounded-xl border border-[#E2E8F0] bg-white text-sm outline-none focus:border-[#4F46E5] transition-colors duration-200" />
    </div>
  );
}
